<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useClipsStore } from '../../stores/clipsStore';
import { useAuthStore } from '../../stores/authStore';
import { useRouter } from 'vue-router';

const clipsStore = useClipsStore();
const authStore = useAuthStore();
const router = useRouter();

// Inline editing state
const editingActorId = ref<number | null>(null);
const editingActorName = ref('');
const editingTagId = ref<number | null>(null);
const editingTagName = ref('');

// Show "All" option + actors with clips
const actorOptions = computed(() => {
  const options: Array<{ id: number | null; name: string; slug: string; clip_count: number }> = [
    { id: null, name: 'All', slug: 'all', clip_count: 0 },
  ];

  // Add actors that have clips, sorted by clip count
  const actorsWithClips = clipsStore.actors
    .filter(a => (a.clip_count ?? 0) > 0)
    .sort((a, b) => (b.clip_count ?? 0) - (a.clip_count ?? 0));
  
  actorsWithClips.forEach(actor => {
    options.push({
      id: actor.id,
      name: actor.name,
      slug: actor.slug,
      clip_count: actor.clip_count ?? 0,
    });
  });

  return options;
});

// Featured tags to show (exclude redundant ones)
const featuredTags = ['trending', 'comedy', 'dialogue', 'punch-dialogue', 'meme', 'reaction', 'roast', 'angry', 'sarcastic'];

const tagOptions = computed(() => {
  return clipsStore.tags
    .filter(t => featuredTags.includes(t.slug))
    .sort((a, b) => featuredTags.indexOf(a.slug) - featuredTags.indexOf(b.slug));
});

function isActorSelected(actorId: number | null): boolean {
  if (actorId === null) {
    return clipsStore.selectedActorId === null && clipsStore.selectedTag === null;
  }
  return clipsStore.selectedActorId === actorId;
}

function isTagSelected(tagSlug: string): boolean {
  return clipsStore.selectedTag === tagSlug;
}

function selectActor(actorId: number | null) {
  if (actorId === null) {
    clipsStore.clearFilters();
  } else {
    clipsStore.setSelectedActor(actorId);
  }
  clipsStore.fetchClips();
}

function selectTag(tagSlug: string) {
  clipsStore.setSelectedTag(tagSlug);
  clipsStore.fetchClips();
}

// Inline edit functions
function startEditActor(e: Event, id: number, name: string) {
  e.stopPropagation();
  editingActorId.value = id;
  editingActorName.value = name;
}

async function saveActorEdit() {
  if (!editingActorId.value || !editingActorName.value.trim()) return;
  try {
    await clipsStore.updateActor(editingActorId.value, editingActorName.value.trim());
    editingActorId.value = null;
    editingActorName.value = '';
  } catch (e) {
    console.error('Failed to save actor:', e);
  }
}

function cancelActorEdit() {
  editingActorId.value = null;
  editingActorName.value = '';
}

function startEditTag(e: Event, id: number, name: string) {
  e.stopPropagation();
  editingTagId.value = id;
  editingTagName.value = name;
}

async function saveTagEdit() {
  if (!editingTagId.value || !editingTagName.value.trim()) return;
  try {
    await clipsStore.updateTag(editingTagId.value, editingTagName.value.trim());
    editingTagId.value = null;
    editingTagName.value = '';
  } catch (e) {
    console.error('Failed to save tag:', e);
  }
}

function cancelTagEdit() {
  editingTagId.value = null;
  editingTagName.value = '';
}

function goToManage() {
  router.push('/admin/manage');
}

onMounted(() => {
  clipsStore.fetchActors();
  clipsStore.fetchTags();
});
</script>

<template>
  <div class="space-y-3">
    <!-- Actors Row -->
    <div class="flex gap-2 overflow-x-auto no-scrollbar py-1 items-center">
      <!-- Admin manage button -->
      <button
        v-if="authStore.isAdmin"
        @click="goToManage"
        class="shrink-0 p-1.5 rounded-full text-slate-500 hover:text-primary hover:bg-primary/10 transition-all"
        title="Manage Actors & Tags"
      >
        <span class="material-symbols-outlined text-lg">settings</span>
      </button>

      <template v-for="actor in actorOptions" :key="`actor-${actor.slug}`">
        <!-- Inline edit mode (only for real actors, not "All") -->
        <div v-if="actor.id !== null && editingActorId === actor.id" class="shrink-0 flex items-center gap-1 bg-console-panel border border-primary rounded-full px-2 py-0.5">
          <input
            v-model="editingActorName"
            class="w-24 bg-transparent text-[11px] font-bold uppercase tracking-wide outline-none"
            @keyup.enter="saveActorEdit"
            @keyup.escape="cancelActorEdit"
            autofocus
          />
          <button @click="saveActorEdit" class="text-green-400 hover:text-green-300">
            <span class="material-symbols-outlined text-sm">check</span>
          </button>
          <button @click="cancelActorEdit" class="text-slate-400 hover:text-white">
            <span class="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
        <!-- Normal button -->
        <button
          v-else
          @click="selectActor(actor.id)"
          @dblclick="authStore.isAdmin && actor.id !== null ? startEditActor($event, actor.id, actor.name) : null"
          :class="[
            'shrink-0 px-4 py-1.5 rounded-full text-[11px] font-bold border uppercase tracking-wide transition-all group',
            isActorSelected(actor.id)
              ? 'bg-primary text-white border-primary shadow-[0_0_15px_rgba(230,0,119,0.3)]'
              : 'bg-console-panel text-slate-400 border-console-border hover:border-slate-500 hover:text-white'
          ]"
          :title="authStore.isAdmin && actor.id !== null ? 'Double-click to edit' : ''"
        >
          {{ actor.name }}
          <span v-if="actor.clip_count > 0" class="ml-1 opacity-60">({{ actor.clip_count }})</span>
        </button>
      </template>
    </div>

    <!-- Tags Row -->
    <div class="flex gap-2 overflow-x-auto no-scrollbar py-1 items-center">
      <template v-for="tag in tagOptions" :key="`tag-${tag.slug}`">
        <!-- Inline edit mode -->
        <div v-if="editingTagId === tag.id" class="shrink-0 flex items-center gap-1 bg-console-panel border border-cyan-500 rounded-md px-2 py-0.5">
          <span class="text-cyan-400 text-[10px]">#</span>
          <input
            v-model="editingTagName"
            class="w-20 bg-transparent text-[10px] font-medium outline-none"
            @keyup.enter="saveTagEdit"
            @keyup.escape="cancelTagEdit"
            autofocus
          />
          <button @click="saveTagEdit" class="text-green-400 hover:text-green-300">
            <span class="material-symbols-outlined text-sm">check</span>
          </button>
          <button @click="cancelTagEdit" class="text-slate-400 hover:text-white">
            <span class="material-symbols-outlined text-sm">close</span>
          </button>
        </div>
        <!-- Normal button -->
        <button
          v-else
          @click="selectTag(tag.slug)"
          @dblclick="authStore.isAdmin ? startEditTag($event, tag.id, tag.name) : null"
          :class="[
            'shrink-0 px-3 py-1 rounded-md text-[10px] font-medium border transition-all',
            isTagSelected(tag.slug)
              ? 'bg-cyan-500/20 text-cyan-400 border-cyan-500/50'
              : 'bg-console-panel/50 text-slate-500 border-console-border/50 hover:border-cyan-500/30 hover:text-cyan-400'
          ]"
          :title="authStore.isAdmin ? 'Double-click to edit' : ''"
        >
          #{{ tag.name }}
        </button>
      </template>
    </div>
  </div>
</template>
